import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import type { UserAvatar } from '@/graphql/schema'
import type { SupportedLanguage } from '@/i18n/language'
import {
  AvatarSprite,
  avatarSpriteGrid,
  getAvatarLabel,
} from '@/app/shell/avatar-sprite'
import { Button } from '@/shared/ui/button'
import { cn } from '@/shared/lib/utils'
import { Label } from '@/shared/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select'
import { SheetClose, SheetFooter } from '@/shared/ui/sheet'
import { SUPPORTED_LANGUAGES } from '@/i18n/language'

type LogoutPhase = 'idle' | 'confirming' | 'armed' | 'loggingOut'

function useLanguageLabels(): Record<SupportedLanguage, string> {
  const { t } = useTranslation()

  return {
    en: t('profile:english'),
    ru: t('profile:russian'),
    es: t('profile:spanish'),
  }
}

function AvatarField({
  disabled,
  onAvatarChange,
  selectedAvatar,
}: {
  disabled: boolean
  onAvatarChange: (avatar: UserAvatar) => void
  selectedAvatar: UserAvatar | null
}) {
  const { t } = useTranslation()

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="space-y-1">
        <Label>{t('profile:avatar')}</Label>
        <p className="text-sm text-muted-foreground">
          {t('profile:avatarHint')}
        </p>
      </div>
      <div
        aria-label="Avatar options"
        className="min-h-0 flex-1 overflow-y-auto pr-1 pb-3"
      >
        <div className="grid grid-cols-3 gap-3">
          {avatarSpriteGrid.map(({ avatar }) => {
            const isSelected = avatar === selectedAvatar

            return (
              <button
                key={avatar}
                type="button"
                aria-label={getAvatarLabel(avatar)}
                aria-pressed={isSelected}
                disabled={disabled}
                className={cn(
                  'flex items-center justify-center rounded-2xl border bg-card p-2 transition-colors',
                  isSelected
                    ? 'border-primary ring-2 ring-primary/20'
                    : 'border-border hover:bg-accent',
                )}
                onClick={() => onAvatarChange(avatar)}
              >
                <AvatarSprite avatar={avatar} size={72} />
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function LanguageSelectField({
  disabled,
  onLanguageChange,
  selectedLanguage,
}: {
  disabled: boolean
  onLanguageChange: (language: SupportedLanguage) => void
  selectedLanguage: SupportedLanguage
}) {
  const { t } = useTranslation()
  const languageLabels = useLanguageLabels()

  return (
    <div className="grid gap-3">
      <Label htmlFor="sheet-language">{t('profile:language')}</Label>
      <Select
        disabled={disabled}
        value={selectedLanguage}
        onValueChange={onLanguageChange}
      >
        <SelectTrigger id="sheet-language" className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SUPPORTED_LANGUAGES.map((lang) => (
            <SelectItem key={lang} value={lang}>
              {languageLabels[lang]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function LogoutButton({
  logoutPhase,
  onLogout,
}: {
  logoutPhase: LogoutPhase
  onLogout: () => void
}) {
  const { t } = useTranslation()

  const label = getLogoutButtonLabel(logoutPhase, t)
  const variant = getLogoutButtonVariant(logoutPhase)

  const disabled = logoutPhase === 'loggingOut'

  return (
    <Button variant={variant} onClick={onLogout} disabled={disabled}>
      {label}
    </Button>
  )
}

function getLogoutButtonLabel(
  logoutPhase: LogoutPhase,
  t: ReturnType<typeof useTranslation>['t'],
) {
  if (logoutPhase === 'loggingOut') return t('profile:loggingOut')
  if (logoutPhase === 'confirming' || logoutPhase === 'armed')
    return t('profile:confirmLogOut')

  return t('profile:logOut')
}

function getLogoutButtonVariant(logoutPhase: LogoutPhase) {
  return logoutPhase === 'confirming' || logoutPhase === 'armed'
    ? 'destructive'
    : 'outline'
}

function ProfileSheetFooter({
  logoutPhase,
  onClose,
  onLogout,
}: {
  logoutPhase: LogoutPhase
  onClose: () => void
  onLogout: () => void
}) {
  const { t } = useTranslation()

  return (
    <SheetFooter className="shrink-0 border-t">
      <div className="flex items-center gap-2">
        <Button asChild>
          <Link to="/settings/profile" onClick={onClose}>
            {t('profile:profileSettings')}
          </Link>
        </Button>
        <SheetClose asChild>
          <Button variant="outline" onClick={onClose}>
            {t('common:close')}
          </Button>
        </SheetClose>
        <LogoutButton logoutPhase={logoutPhase} onLogout={onLogout} />
      </div>
    </SheetFooter>
  )
}

type AppSheetContentProps = {
  displayName: string
  email: string
  isSaving: boolean
  logoutPhase: LogoutPhase
  onAvatarChange: (avatar: UserAvatar) => void
  onClose: () => void
  onLanguageChange: (language: SupportedLanguage) => void
  onLogout: () => void
  selectedAvatar: UserAvatar | null
  selectedLanguage: SupportedLanguage
}

export function AppSheetContent({
  displayName,
  email,
  isSaving,
  logoutPhase,
  onAvatarChange,
  onClose,
  onLanguageChange,
  onLogout,
  selectedAvatar,
  selectedLanguage,
}: AppSheetContentProps) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-hidden px-4">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{displayName}</p>
          <p className="truncate text-sm text-muted-foreground">{email}</p>
        </div>
        <LanguageSelectField
          disabled={isSaving}
          onLanguageChange={onLanguageChange}
          selectedLanguage={selectedLanguage}
        />
        <AvatarField
          disabled={isSaving}
          onAvatarChange={onAvatarChange}
          selectedAvatar={selectedAvatar}
        />
      </div>
      <ProfileSheetFooter
        logoutPhase={logoutPhase}
        onClose={onClose}
        onLogout={onLogout}
      />
    </div>
  )
}
